import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

interface SystemCustomization {
  id?: string;
  companyId?: string;
  logoUrl?: string;
  systemName: string;
  createdAt?: string;
  updatedAt?: string;
}

interface CustomizationContextType {
  customization: SystemCustomization;
  applyCustomization: (customization: SystemCustomization) => void;
  loadCustomization: () => Promise<void>;
}

const defaultCustomization: SystemCustomization = {
  systemName: 'Cobrança'
};

const CustomizationContext = createContext<CustomizationContextType | undefined>(undefined);

export const useCustomization = () => {
  const context = useContext(CustomizationContext);
  if (context === undefined) {
    throw new Error('useCustomization must be used within a CustomizationProvider');
  }
  return context;
};

const applyCustomizationToDOM = (customization: SystemCustomization) => {
  // Atualizar o nome do sistema no título
  if (customization.systemName) {
    const titleElements = document.querySelectorAll('[data-system-name]');
    titleElements.forEach(el => {
      el.textContent = customization.systemName;
    });
    
    // Atualizar o título da página
    document.title = `${customization.systemName} - Sistema de Cobrança`;
  }
};

export const CustomizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customization, setCustomization] = useState<SystemCustomization>(defaultCustomization);
  const [isLoaded, setIsLoaded] = useState(false);
  const { user, isAuthenticated } = useAuthStore();

  const loadCustomization = async () => {
    if (isLoaded) return; // Prevent multiple loads
    
    // Só carregar do servidor se o usuário estiver autenticado
    if (isAuthenticated && user) {
      try {
        const response = await api.get('/config/customization');
        if (response.data.success && response.data.data) {
          const savedCustomization = response.data.data;
          setCustomization(savedCustomization);
          applyCustomizationToDOM(savedCustomization);
          setIsLoaded(true);
          return;
        }
      } catch (error) {
        console.log('Nenhuma personalização encontrada no servidor, usando localStorage...');
      }
    }
    
    // Fallback para localStorage
    const saved = localStorage.getItem('systemCustomization');
    if (saved) {
      try {
        const savedCustomization = JSON.parse(saved);
        setCustomization(savedCustomization);
        applyCustomizationToDOM(savedCustomization);
      } catch (error) {
        console.error('Erro ao carregar personalização do localStorage:', error);
      }
    }
    setIsLoaded(true);
  };

  const applyCustomization = (newCustomization: SystemCustomization) => {
    setCustomization(newCustomization);
    applyCustomizationToDOM(newCustomization);
  };

  useEffect(() => {
    loadCustomization();
  }, [isAuthenticated, user]); // Executar quando o usuário autenticar

  return (
    <CustomizationContext.Provider value={{ customization, applyCustomization, loadCustomization }}>
      {children}
    </CustomizationContext.Provider>
  );
};
